// 📦 Core Libraries & 🧠 State Management
import React from 'react';
import { makeAutoObservable, observable, action, computed } from 'mobx';
import { 
  IDesignTreeEntry,
  IBlockMgmt,
  IPreviewMode, 
  IGlideBarState, 
  IPageContent,
  IBodyStyles, 
  ITypoStyles, 
  ILayoutStyles,
  ITypographyRole,
  IFourSideField,
  IFourCornersField
} from "./state-interfaces";
import contentHistoryManager from './content-history-manager';
import { IImageAsset } from '../components/ui/shared/draggable-asset';

/**
 * PageBuilderStore – Central Reactive State Manager for the PageBuilder Application
 *
 * @description
 * This MobX store orchestrates all application state across the PageBuilder UI, including:
 * layout and typography configuration, content block management, serialization for preview/publish,
 * floating GlideBar editing, tab selection, and live autosave functionality.
 *
 * It provides one source of truth that binds the SidebarPanel, DesignCanvas, PreviewToolbar,
 * TopBar, CodeEditor, and related builder tools together.
 *
 * @structure
 * - Core metadata: page title, publication state, directory path
 * - Live preview configuration (previewMode)
 * - Sidebar bodySettings (layout + typography)
 * - Content management via `pageContent` (html, json, css, blocks)
 * - Rich text formatting state via `glideBarState`
 * - Autosave with toast notifications
 *
 * @usage
 * This store is injected globally via React Context and made observable using MobX.
 *
 * @example
 * import pageBuilderStore from "../stores/pageBuilderStore"
 * pageBuilderStore.setPageTitle("About Us")
 * pageBuilderStore.setActiveBlock("id-1234", "heading", ref)
 *
 * @since 2025-06-08
 */
class PageBuilderStore {
  // ───────────────────────────────────────────────
  // 1. Core Properties & Setters
  
  /** Whether this builder instance is rendering an email template (vs a web page). */
  isEmail = false;

  customFields: string[] = [];

  addedFields = observable.map<string, string[]>({}, { deep:false });
  
  /** Title of the page being edited. Used in TopBar. */
  pageTitle = "Untitled Page";

  /** Organization initials that can be used for (de)serialization operations. */
  orgInitials = "";

  isEditingContent = false;
  
  /** Indicates if the page is currently being saved as draft. */
  isSavingDraft = false;
  
  /** Whether the current page has been published (live). */
  isPublished = false;
  
  /** Currently selected tab in the editor: design | html | styles | text */
  selectedTab: string = "Design";
  
  /** Persistent status of the page (draft or published) */
  pageStatus: "draft" | "published" = "draft";
  
  /** Directory path or logical folder this page belongs to */
  currDir = "Trainers / Keith Tan";
  
  /** Currently active Sidebar panel, if any */
  activePanel: string = "content";
  
  /** Whether autosave is currently in progress */
  isAutoSaving = false;
  
  /** Message shown in toast notifications (autosave feedback, etc.) */
  toastMessage = "";

  /** Where we keep/manage a global set of images/photos for the Image Library */
  imageLibraryAssets: IImageAsset[] = [];

  /**
   * Initializes the PageBuilderStore and applies MobX auto-observability.
   */
  constructor() {
    makeAutoObservable(this, {
      addedFields: observable,
      updateAddedFields: action.bound,
      removeAddedField: action.bound,
      removeAddedFields: action.bound,
      getAllAddedFields: computed
    });
  }

  // ───────────────────────────────────────────────
  // 2. Preview Mode

  /**
   * Stores the preview configuration state used in PreviewToolbar and DesignCanvas.
   *
   * @description
   * Determines whether preview mode is active, what screen width is selected,
   * and the simulated device type (desktop, tablet, or phone).
   */
  previewMode: IPreviewMode = {
    showPreview: false,
    selectedWidth: "",
    deviceMode: "desktop",
  };

  /**
   * Updates preview mode configuration (width, device, or visibility).
   */
  setPreviewMode(update: Partial<typeof this.previewMode>) {
    Object.assign(this.previewMode, update);
  }

  // ───────────────────────────────────────────────
  // 3. Body Styles (Sidebar > Layout & Typography)

  DEFAULT_PADDING: IFourSideField = {
    top: "32",
    right: "32",
    bottom: "32",
    left: "32",
    unit: "px",
    linked: true
  };

  DEFAULT_BORDER_RADIUS: IFourCornersField = {
    topLeft: "0",
    topRight: "0",
    bottomRight: "0",
    bottomLeft: "0",
    unit: "px",
    linked: true
  };

  // ---- 1. Define your core roles somewhere central ----
  CORE_TYPOGRAPHY_ROLES: Record<string, ITypographyRole> = {
    title: {/* ... */},
    h1: {/* ... */},
    h2: {/* ... */},
    h3: {/* ... */},
    h4: {/* ... */},
    h5: {/* ... */},
    h6: {/* ... */},
    p: {/* ... */},
    caption: {/* ... */},
    quote: {/* ... */},
    links: {/* ... */}
    // ...complete as needed
  };

  /**
   * Stores layout and typography configurations shown in the Sidebar panel.
   *
   * @description
   * - `layoutState`: manages content width, container type, padding, and alignment
   * - `typoState`: maps text roles (h1–h6, body, caption, quote) to fontSize strings
   *
   * Used by SidebarPanel sections to live-update styling and also exported via `fromBlocksToCSS()`.
   */
  bodyStylesState: IBodyStyles = {
    layout: undefined,
    typography: undefined,
    activeTextRole: "body",
    activeHeadingLevel: "h1",
  };

  // ───────────────────────────────────────────────
  // 4. Page Content (Blocks + HTML/JSON)

  blockMgmtState: IBlockMgmt = {
    blockEditing:{},
    heroSlideEditing: {
      selectedSlideIndex: 0,
      selectedSlide: undefined,
    },
    selectedImage: undefined,
    openGalleryDialog: false,
    isDraggingBlock: false,
    dropHandled: false,
    hoveredDropZoneId: null,
    draggingBlockId: null,
    hoveredTargetId: null,
    draggingFromColIndex: null,
    fromDesignBlockId: null,
    designTree: new Map<string, IDesignTreeEntry>()
  };

  /**
   * Stores all serialized and structured content for the current page.
   *
   * @description
   * Holds the four main content representations:
   * - htmlContent: raw HTML string for preview/publish
   * - jsonContent: block-based JSON model for builder rehydration
   * - cssContent: array of CSS declarations used for styling/export
   * - blockContent: live MobX-managed array of block data
   *
   * This unified structure enables saving, exporting, and editing across the builder lifecycle.
   */
  pageContent: IPageContent = {
    htmlContent: '',
    jsonContent: {},
    cssContent: [],
    designContent: []
  };

  // ───────────────────────────────────────────────
  // 5. GlideBar State

  /**
   * UI state for the floating GlideBar formatting toolbar.
   * Tracks active block, selection state, and dropdown visibility.
   */
  glideBarState: IGlideBarState = {
    isVisible: false,
    contentBlockId: null,
    blockType: null,
    contentBlockRef: null,
    isEditingText: false,
    isEditingHeading: false,
    currCaretPos: null,
    isDropDownOpen: false,
    currentHeading: "H1"
  };

  // ───────────────────────────────────────────────
  // 6. Autosave Logic

  /**
   * Sets the toast message and resets it after 3 seconds.
   */
  setToast(message: string) {
    this.toastMessage = message;
    setTimeout(() => {
      this.toastMessage = "";
    }, 3000);
  }

  // ───────────────────────────────────────────────
  // 7. Misc Setters

  /**
   * Sets whether the current project is an email (affects restrictions and layout).
   */
  setIsEmail(isEmail: boolean) {
    this.isEmail = isEmail;
  }

  /**
   * Determines whether the current PageBuilder instance is operating in email template mode.
   *
   * @returns {boolean} True if the builder is in email mode; otherwise, false.
   *
   * @example
   * if (pageBuilderStore.getIsEmail()) {
   *   // Show email-specific UI or behaviors
   * }
   */
  getIsEmail(): boolean {
    return this.isEmail;
  }

  /**
   * Sets the array of custom field merge tags available for insertion in the PageBuilder.
   * Typically used to provide user- or system-defined placeholders for personalization (e.g., {first-name}, {email}).
   *
   * @param {string[]} fields - An array of custom field strings to be used as merge tags.
   *
   * @example
   * pageBuilderStore.setCustomFields(['{first-name}', '{last-name}', '{membership-type}']);
   */
  setCustomFields(fields: string[]) {
    this.customFields = fields;
  }

  /**
   * Retrieves the current array of custom field merge tags available for insertion.
   * These fields can be used for dynamic content personalization in the PageBuilder (e.g., {first-name}, {email}).
   *
   * @returns {string[]} An array of custom field strings.
   *
   * @example
   * const fields = pageBuilderStore.getCustomFields();
   * // fields might be: ['{first-name}', '{last-name}', '{membership-type}']
   */
  getCustomFields(): string[] {
    return this.customFields;
  }

  /**
   * Updates the list of custom fields that have been added to a specific content block.
   * Typically used to track which merge tags have been inserted for dynamic personalization.
   *
   * @param {string} blockId - The unique identifier of the content block being updated.
   * @param {string[]} fields - An array of custom field strings that have been added to the block.
   *
   * @example
   * // After user inserts two custom fields into block 'heading-1'
   * pageBuilderStore.updateAddedFields('heading-1', ['{first-name}', '{membership-type}']);
   */
  updateAddedFields(blockId: string, fields: string[]) {
    this.addedFields.set(blockId, [...fields]);
  }

  /**
   * Retrieves the list of custom fields (merge tags) that have been added to the specified content block.
   * Useful for tracking which dynamic placeholders are present in a given block.
   *
   * @param {string} blockId - The unique identifier of the content block to query.
   * @returns {string[]} An array of custom field strings that have been added to the block.
   *
   * @example
   * const added = pageBuilderStore.getAddedFields('heading-1');
   * // added might be: ['{first-name}', '{membership-type}']
   */
  getAddedFields(blockId: string): string[] {
    return this.addedFields.get(blockId) || [];
  }

  /**
   * Deletes/removes any custom fields that may have already been added to the specified content block.
   * @param {string} blockId - The unique identifier of the content block to query.
   */
  removeAddedFields(blockId: string) {
    this.addedFields.delete(blockId);
  }

  /**
   * Deletes a single custom field which has been added to this block.
   * @param {string} blockId - The unique identifier of the content block to query.
   * @param {string} fieldValue - The unique custom field used for inserting custom data. 
   * @returns Only if there are no custom fields associated with the content block.
   */
  removeAddedField(blockId: string, fieldValue: string) {
    const fields = this.addedFields.get(blockId);
    if (!fields) return;

    // Remove the fieldValue from the array
    const updatedFields = fields.filter(field => field !== fieldValue);

    // Update the Map
    if (updatedFields.length > 0) {
      console.log("updated added fields for", blockId, "Num Fields:", (this.addedFields.get(blockId) ?? []).filter(field => field !== fieldValue).length);
      this.addedFields.set(blockId, (this.addedFields.get(blockId) ?? []).filter(field => field !== fieldValue));
    } else {
      this.addedFields.delete(blockId);
    }
  }

  /**
   * Retrieves a deduplicated array of all custom fields (merge tags) that have been added
   * across all content blocks in the current PageBuilder state.
   *
   * @returns {string[]} An array of unique custom field strings added throughout the page.
   *
   * @example
   * const allFields = pageBuilderStore.getAllAddedFields();
   * // allFields might be: ['{first-name}', '{membership-type}', '{email}']
   */
  get getAllAddedFields(): string[] {
    // derived fresh union from observable map every time
    const uniq = new Set<string>();
    for (const arr of this.addedFields.values()) {
      // arr is a fresh array we set above
      for (const f of arr) uniq.add(f);
    }
    return Array.from(uniq);
  }

  get getAllAddedFieldsWithOwners(): Array<{ blockId: string, fields: string[] }> {
    const out: Array<{ blockId: string, fields: string[] }> = [];
    for (const [bid, arr] of this.addedFields.entries()) {
      out.push({ blockId: bid, fields: [...arr] });
    }
    return out;
  }

  /**
   * Sets the organization initials to be used as a prefix in custom CSS selector names,
   * ensuring CSS class and selector uniqueness across different organizations or tenants.
   *
   * @param {string} initials - The organization initials or short code to use for namespacing CSS selectors.
   *
   * @example
   * // Use 'rzv' to generate selectors like '.rzv-header'
   * pageBuilderStore.setOrgInitials('rzv');
   */
  setOrgInitials(initials: string) {
    this.orgInitials = initials;
  }

  /**
   * Retrieves the current organization initials used for prefixing custom CSS selectors.
   * This value helps namespace and uniquely identify selectors across different organizations or tenants.
   *
   * @returns {string} The organization initials or short code currently in use.
   *
   * @example
   * const initials = pageBuilderStore.getOrgInitials();
   * // initials might be: 'rzv'
   */
  getOrgInitials(): string {
    return this.orgInitials;
  }

  /**
   * Sets the editing state of the PageBuilder to indicate whether the user is currently
   * creating a new page or editing existing content. This can control UI flows, autosave,
   * or behavior that differs between new and edit modes.
   *
   * @param {boolean} isEditing - True if the user is editing existing content; false if creating a new page.
   *
   * @example
   * // Mark the builder as editing existing content
   * pageBuilderStore.setIsEditingContent(true);
   */
  setIsEditingContent(isEditing: boolean) {
    this.isEditingContent = isEditing;
  }

  /**
   * Returns whether the PageBuilder is currently in editing mode
   * (editing existing content vs. creating a new page).
   *
   * @returns {boolean} True if editing existing content; false if creating a new page.
   *
   * @example
   * if (pageBuilderStore.getIsEditingContent()) {
   *   // Show "Save Changes" button instead of "Create Page"
   * }
   */
  getIsEditingContent(): boolean {
    return this.isEditingContent;
  }

  /**
   * Updates the title of the page being edited.
   */
  setPageTitle(title: string) {
    this.pageTitle = title;
  }

  /**
   * Controls the draft saving state for visual feedback.
   */
  setIsSavingDraft(isSaving: boolean) {
    this.isSavingDraft = isSaving;
  }

  /**
   * Sets whether the page is marked as published.
   */
  setIsPublished(isPublished: boolean) {
    this.isPublished = isPublished;
  }

  /**
   * Sets the currently selected editor tab.
   */
  setSelectedTab(tab: string) {
    this.selectedTab = tab;
  }

  /**
   * Sets the page status label (draft or published).
   */
  setPageStatus(status: "draft" | "published") {
    this.pageStatus = status;
  }

  /**
   * Sets the current directory/folder the page belongs to.
   */
  setCurrDir(dir: string) {
    this.currDir = dir;
  }

  /**
   * Sets which Sidebar panel is currently visible (null to close all).
   */
  setActivePanel(panel: string) {
    this.activePanel = panel;
  }

  /**
   * Toggles the auto saving mode and to temporarily save the user's editing content.
   */
  toggleIsAutoSaving() {
    this.isAutoSaving = !this.isAutoSaving;
  }

  /**
   * Sets the global image library assets for the PageBuilder, replacing any existing assets.
   * Typically called when initializing the builder or after fetching new image data from the server.
   *
   * @param {IImageAsset[]} assets - An array of image asset objects to set as the current image library.
   *
   * @example
   * // Initialize the image library with a fetched list of assets
   * pageBuilderStore.setImageLibraryAssets([
   *   { url: '/img/banner.jpg' },
   *   { url: '/img/logo.png' }
   * ]);
   */
  setImageLibraryAssets(assets: IImageAsset[]) {
    this.imageLibraryAssets = assets;
  }

  /**
   * Appends new image asset objects to the existing global image library.
   * Useful for adding images after a successful upload or import, without replacing the entire library.
   *
   * @param {IImageAsset[]} newAssets - An array of new image asset objects to add to the image library.
   *
   * @example
   * // Add newly uploaded images to the image library
   * pageBuilderStore.addToImageLibraryAssets([
   *   { url: '/img/new1.jpg' },
   *   { url: '/img/new2.jpg' }
   * ]);
   */
  addToImageLibraryAssets(newAssets: IImageAsset[]) {
    this.imageLibraryAssets.push(...newAssets);
  }

  /**
   * Retrieves the current array of global image library assets available in the PageBuilder.
   *
   * @returns {IImageAsset[]} An array of image asset objects in the image library.
   *
   * @example
   * const assets = pageBuilderStore.getImageLibraryAssets();
   * // assets might be: [{ url: '/img/banner.jpg' }, { url: '/img/logo.png' }]
   */
  getImageLibraryAssets() {
    return this.imageLibraryAssets;
  }
}

const pageBuilderStore = new PageBuilderStore();
export default pageBuilderStore;
